import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { ul } from './helpers';
import {
  COMPANY_QUALITIES_NAMES,
  COMPETITIVE_ANALYSIS_NAMES,
  COMPETITORS_COMPARISON_NAMES,
  FEATURES_FOR_COMPARISON_NAMES,
  FEATURES_LIST_NAMES,
  KEY_COMPETITORS_NAMES,
} from '../Sections/MarketAnalysis/CompetitiveAnalysis/utils/form';
import { B2B_NAMES, B2C_NAMES, CONSUMER_NEEDS_NAMES } from '../Sections/MarketAnalysis/ConsumerNeeds/utils/form';
import {
  AVERAGE_PRICES_CHART_NAMES,
  CONSUMER_DETAILS_NAMES,
  CURRENT_MARKET_STRUCTURE_NAMES,
  FORECASTED_CONSUMPTION_CHART_NAMES,
  MARKET_ANALYSIS_OVERVIEW_NAMES,
  MARKET_TRENDS_FIELD_NAMES,
  REGION_PERIODS_NAMES,
  REGIONS_PRODUCT_NAMES,
} from '../Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';

const YEARS_ROW_INSTRUCTIONS = {
  name: 'Value',
  instruction: 'Specify the value of the market, ensure it is accurate and clear.',
};

const CONSUMPTION_TABLE_DATA_INSTRUCTIONS = {
  [REGION_PERIODS_NAMES.Y1]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y2]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y3]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y4]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y5]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.Y6]: YEARS_ROW_INSTRUCTIONS,
  [REGION_PERIODS_NAMES.REGION]: {
    name: 'Name',
    instruction: 'Specify the exact name of the market, ensure it is accurate and clear.',
    exampleOne: 'USA',
    exampleTwo: 'Europe',
  },
};

const FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS = {
  [FORECASTED_CONSUMPTION_CHART_NAMES.TITLE]: {
    name: 'Consumption Forecast',
    instruction: 'Provide a clear and concise title for the chart that accurately reflects its content. ',
    exampleOne: 'European market of financial services',
    exampleTwo: 'American market of agriculture equipment',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.REGION]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [FORECASTED_CONSUMPTION_CHART_NAMES.TABLE_ROWS]: CONSUMPTION_TABLE_DATA_INSTRUCTIONS,
  [FORECASTED_CONSUMPTION_CHART_NAMES.SOURCE]: {
    name: 'Data Source',
    instruction: 'Clearly state the reliable origin or method used to gather information. ',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const CONSUMER_DETAILS_INSTRUCTIONS = {
  [CONSUMER_DETAILS_NAMES.CONSUMER]: {
    name: 'Consumer',
    instruction: 'Specify the exact name of the segment, ensure it is accurate and clear.',
    exampleOne: 'USA\n' + 'Europe\n' + 'Asia',
    exampleTwo: 'Farmers\n' + 'Аgro-enterprises',
  },
  [CONSUMER_DETAILS_NAMES.PERCENTAGE_SHARE]: {
    name: 'Percentage Share',
    instruction:
      'Detail the share of each segment. Provide percentages for each segment and ensure the total equals 100%.',
    exampleOne: '',
    exampleTwo: '',
  },
  [CONSUMER_DETAILS_NAMES.PHYSICAL_SHARE]: {
    name: 'Physical Share',
    instruction: 'Specify the share of each segment in physical units. Provide clear, precise figures.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const MARKET_TRENDS_INSTRUCTIONS = {
  [MARKET_TRENDS_FIELD_NAMES.TREND_NAME]: {
    name: 'Trend Name',
    instruction:
      'Identify the main market trends. Ensure the trends are relevant to your industry and reflect current market dynamics.',
    exampleOne: 'Focus on Sustainability',
    exampleTwo: 'Specialization in Niche Industries',
  },
  [MARKET_TRENDS_FIELD_NAMES.TREND_CHARACTERISTICS]: {
    name: 'Trend Analysis',
    instruction:
      'Provide a snapshot of the current market trends in your industry. Identify and outline the prevailing shifts, preferences, or behaviours shaping the landscape. Highlight recent developments influencing consumer choices, industry practices, or technological advancements.',
    exampleOne:
      'Manufacturers are developing environmentally friendly equipment with reduced emissions. Additionally, there is a growing demand for machinery supporting sustainable farming practices, such as conservation tillage and organic farming.',
    exampleTwo:
      'There is a growing trend towards specialization in niche industries such as legal, medical, technical, and marketing translation, as clients seek translators with expertise in specific subject matter areas.',
  },
};

const CURRENT_MARKET_STRUCTURE_INSTRUCTIONS = {
  [CURRENT_MARKET_STRUCTURE_NAMES.TITLE]: {
    name: 'Chart title',
    instruction: 'Provide a clear and concise title for the chart that accurately reflects its content. ',
    exampleOne: 'Geographical market structure',
    exampleTwo: 'Market structure by consumers',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER]: {
    name: '',
    instruction: 'Specify the exact name of the serment, ensure it is accurate and clear.',
    exampleOne: `USA
    Europe
    Asia`,
    exampleTwo: `Farmers
    Аgro-enterprises`,
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.DATE]: {
    name: '',
    instruction: 'Specify the actual date for the information, including the month and year.',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.UNITS]: {
    name: '',
    instruction: '',
    exampleOne: '',
    exampleTwo: '',
  },
  [CURRENT_MARKET_STRUCTURE_NAMES.CONSUMER_DETAILS]: CONSUMER_DETAILS_INSTRUCTIONS,
  [CURRENT_MARKET_STRUCTURE_NAMES.SOURCE]: {
    name: 'Data Source',
    instruction: 'Clearly state the reliable origin or method used to gather information. ',
    exampleOne: 'Eurostat',
    exampleTwo: 'Statista',
  },
};

const PRODUCT_ROW_INSTRUCTIONS = {
  name: 'Product/Service',
  instruction:
    'Specify the exact name of the product or service. Ensure it is accurate and clearly represents the offering, adhering to official naming conventions.',
  exampleOne: 'Tracked Tractors',
  exampleTwo: 'AI-Powered Translation Tools',
};
const AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS = {
  [REGIONS_PRODUCT_NAMES.PRODUCT]: PRODUCT_ROW_INSTRUCTIONS,
};

const MARKET_OVERVIEW_INSTRUCTIONS = {
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MAIN_MARKET_OVERVIEW]: {
    name: 'Market Segmentation',
    instruction:
      'Define the market under consideration. Introduce the key characteristics of the market, emphasizing elements that define its nature. Delve into market barriers and challenges that may impact entry or operations. Provide a concise overview of the market segments, emphasizing geographic and industrial categorizations, customer types, etc.',
    exampleOne:
      'The market under consideration is the agricultural machinery sector. The market is segmented by type of farming machinery: tractors, ploughing, planting, and harvesting machinery. The largest segment is Tractors, as they are universal agricultural machinery.',
    exampleTwo:
      'The language services market serves clients and translators globally. While the market lacks serious entry barriers, it is highly competitive. Segments include written translation, oral translation, subtitling, and voice-over services, with written translation being predominant.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SERVICES_PRODUCTS_SUMMARY]: {
    name: 'Product Summary',
    instruction:
      'Describe the primary services or products offered in your target market. What are their key features, main purposes, and pricing strategies? How do these factors influence market demand and competition?',
    exampleOne:
      'Market offerings include Class A and Class B tractors catering to varied client requirements. Class A tractors prioritize lightweight to minimize soil pressure, while Class B tractors offer high reliability and passability. Pricing begins at $25,000.',
    exampleTwo:
      'Market offerings include human, machine, and AI translation services with varying pricing based on project complexity. Unique selling points include accuracy, efficiency, and cost-effectiveness, catering to diverse client needs.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CONSUMERS_DESCRIPTION]: {
    name: 'Consumer Demographics',
    instruction:
      'Summarize consumer demographics, behavior, and preferences. Utilize data to outline groups, territories, and demand patterns.',
    exampleOne:
      'Consumers include farmers worldwide, predominantly in regions with heavy or wet soils. They seek high-performing tractors with low ground pressure. Demand fluctuates with planting and harvesting seasons.',
    exampleTwo:
      'Consumers include individuals and corporations. Demand is widespread, with significant concentrations in multinational business hubs and regions with high immigrant populations. Preferences lean towards efficient and reliable communication solutions across various industries.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_COMPETITORS]: {
    name: 'Identify your competitors',
    instruction:
      'Identify your competitors in the market by number, highlighting the leaders and their positioning. Analyze strengths and weaknesses, as well as market share to understand the competitive landscape.',
    exampleOne:
      "The market consists of approximately 2,000 tractor manufacturers, with John Deere leading the way with a 20% market share. John Deere's positioning emphasizes reliability and innovation. Competitors vary in size and specialization, each with unique strengths and weaknesses.",
    exampleTwo:
      'There are 15 language services platforms on the market, with T1 and STL leading the way. T1 has the largest market share due to its extensive network of translators and user-friendly interface. STL differentiates itself through its integration of advanced technologies and personalized customer service.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.ACTUAL_AND_FORECASTED_CONSUMPTION]: {
    name: 'Consumption Analysis',
    instruction:
      'Describe consumption trends using statistics. Provide actual data and forecast figures, highlighting growth trajectories and market dynamics.',
    exampleOne:
      'In 2022, the market sold 1 million tracked tractors, with an expected annual growth rate of 5-7%. This growth reflects the increasing mechanization in agriculture and the growing demand for reliable tractors with low environmental impact.',
    exampleTwo:
      'According to a report by consulting company A, the total market for language services is estimated at $54 billion in 2024. The language services market is projected to grow at a CAGR of 17% and reach $78 billion by 2027. The growth trajectories show a steady increase in the demand for language services.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.FORECASTED_CONSUMPTION_CHART]: FORECASTED_CONSUMPTION_CHART_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_SHARE]: {
    name: 'Market Share Insight',
    instruction:
      'Analyze sales data to determine your current market share. Highlight brand influence through customer loyalty, innovation, and market expansion strategies.',
    exampleOne:
      'The Company`s current market share is 0,02%. The Company holds a strong position in the market due to its innovative tracked tractors. With superior technology and cost-effective pricing, the Company has gained significant customer loyalty.',
    exampleTwo:
      'The Company holds a 15% market share in the language services sector. It is competitive in the market, boasting strong brand recognition and a loyal customer base. Innovative technology and diverse payment options give the Company an edge.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_TRENDS]: MARKET_TRENDS_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.SALES_METHOD]: {
    name: 'Sales Strategy',
    instruction:
      'Describe sales channels for the product/service. Detail online platforms, retail partnerships, and direct sales strategies to reach target customers effectively.',
    exampleOne:
      'Typical sales channels include the company website, e-commerce portals, retail partnerships with agricultural equipment stores, and direct sales through company representatives.',
    exampleTwo:
      'Sales Channels use a multi-channel approach, leveraging their online platform, strategic partnerships with language schools, and direct sales strategies that target corporate clients via personalized consultations and demos to optimize communication efficiency.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_STRUCTURE_DESCRIPTION]: {
    name: 'Market Structure Overview',
    instruction: 'Comment on the graph below, indicate key segments',
    exampleOne:
      'In a market for agricultural machinery, key segments includes: Competitors: Major manufacturers like John Deere, Caterpillar, and CNH Industrial. Consumer demographics: Farmers, agricultural cooperatives, and farming corporations.',
    exampleTwo:
      'Key segments might includes: freelance translators, translation agencies, language technology providers, in-house translation departments (corporate), machine translation software companies. ',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.CURRENT_MARKET_STRUCTURE]: CURRENT_MARKET_STRUCTURE_INSTRUCTIONS,
  [MARKET_ANALYSIS_OVERVIEW_NAMES.MARKET_GROWTH_FACTORS]: {
    name: 'Growth Factors',
    instruction:
      'Begin by identifying the key factors expected to contribute to the growth of your market. These may encompass macroeconomic trends, technological advancements, regulatory changes, or shifts in consumer preferences. Offer a concise yet comprehensive overview of the external elements anticipated to influence market expansion positively.',
    exampleOne:
      'Key factors driving market growth include environmental sustainability, advanced technology adoption, and favourable economic conditions. These trends fuel the demand for high-tech, eco-friendly agricultural machinery.',
    exampleTwo:
      'Market growth will be facilitated by the ongoing shift towards remote work and virtual collaboration, accelerated by the COVID-19 pandemic, which is expected to drive demand for remote interpretation services and online translation platforms, supporting market expansion.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.TECHNOLOGY_IMPACT]: {
    name: 'Technology Impact',
    instruction:
      'Assess the impact of technology changes on the industry. Highlight opportunities for innovation, efficiency gains, and disruptions to existing business models.',
    exampleOne:
      'The agricultural machinery industry has significant innovation potential based on technological advancements. Automation, precision farming, and data analytics drive efficiency gains in agriculture. Robotics enhance efficiency by reducing labour dependency.',
    exampleTwo:
      'The language services industry used technological advancements, particularly in AI and machine learning, to enhance translation accuracy and efficiency. Additionally, innovation encompasses the development of specialized tools tailored to specific industries or languages.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.PRICE_STRATEGIES]: {
    name: 'Pricing Strategies',
    instruction:
      'Examine competitors` pricing strategies. Evaluate factors like pricing models, discounts, and value propositions. Tailor pricing strategies accordingly, ensuring competitiveness while preserving profitability.',
    exampleOne:
      'Competitors utilize various pricing strategies such as value-based pricing, penetration pricing, and cost-plus pricing. Discounts may be offered for bulk purchases or seasonal promotions. Tailor pricing to match value propositions, balancing competitiveness and profitability.',
    exampleTwo:
      'Competitors employ diverse pricing strategies like hourly rates for freelancers, per-word charges for agencies, and subscription models for language tech providers. Understanding these strategies informs our competitive pricing decisions.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_TITLE]: {
    name: 'Price Overview',
    instruction: 'Provide a clear and concise title for the chart that accurately reflects its content. ',
    exampleOne: 'Tracked Tractors Prices',
    exampleTwo: 'AI-Powered Translation Tools Prices',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_DATE]: {
    name: '',
    instruction: 'Specify the actual date for the information, including the month and year.',
  },
  [MARKET_ANALYSIS_OVERVIEW_NAMES.AVERAGE_PRICES_CHART]: {
    [AVERAGE_PRICES_CHART_NAMES.AVERAGE_PRICES_REGIONS_ROWS]: AVERAGE_PRICES_TABLE_DATA_INSTRUCTIONS,
  },
};

const KEY_COMPETITORS_INSTRUCTIONS = {
  [KEY_COMPETITORS_NAMES.COMPETITOR]: {
    name: 'Name',
    instruction: 'Provide the intended full and official name of the company.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [KEY_COMPETITORS_NAMES.FILE]: {
    name: '',
    instruction:
      'Insert the company logo. Ensure the logo is high-resolution, appropriately sized and has transparent background.',
    exampleOne: '',
    exampleTwo: '',
  },
  [KEY_COMPETITORS_NAMES.WEBSITE]: {
    name: 'Website',
    instruction: "Share the company's full web address",
    exampleOne: 'agro.tech.innovations.com',
    exampleTwo: 'translink.com',
  },
  [KEY_COMPETITORS_NAMES.LOCATION]: {
    name: 'Location',
    instruction: 'Specify the city and country where the company is officially registered.',
    exampleOne: '134 E Main St, Marion, NC 28752,US',
    exampleTwo: 'Portland, US',
  },
  [KEY_COMPETITORS_NAMES.DESCRIPTION]: {
    name: 'Brief description of the product',
    instruction:
      'Outline the core features that define a product. Highlight any innovative aspects or technological advancements that set it apart in the market. Clearly articulate the functionality, specifications, and design elements that contribute to its uniqueness. Discuss how these features address specific needs or pain points in the target market, emphasizing the value proposition that your product brings.',
    exampleOne:
      'The Company offers various agricultural equipment, including tractors, combines, seeders and sprayers. Main product is powerful tractors that utilize biofuel as their primary fuel source. Built for durability and efficiency, they maximize productivity for farmers.',
    exampleTwo:
      'The competitor provides an advanced language services platform. Core features include real-time collaboration tools and access to a diverse network of professional translators.',
  },
  [KEY_COMPETITORS_NAMES.POSITIONING]: {
    name: 'Positioning',
    instruction:
      'Present an overview of the market positioning of your competitors. Discuss their current standing in the market, emphasizing factors such as brand recognition and overall market influence.',
    exampleOne: 'The Company`s position as a manufacturer promoting ecological and sustainable agricultural practices.',
    exampleTwo:
      'The Company holds a strong position in the market and is known for its established brand and extensive network of translators. focuses on seamless communication, cultural understanding and effective localisation, offering customised translation services to meet the specific needs of its clients.',
  },
  [KEY_COMPETITORS_NAMES.SALES_CHANNELS]: {
    name: 'Sales Channels',
    instruction:
      'Provide an overview of the sales channels utilized by your key competitors. It may include a discussion on their distribution networks, retail partnerships, online platforms, or any other channels through which they reach customers.',
    exampleOne: 'Include dealerships, online platforms, and direct sales to large-scale farms.',
    exampleTwo:
      'The company sells its products through online platforms, its website, industry-specific organizations,  and targeted email marketing campaigns.',
  },
  [KEY_COMPETITORS_NAMES.PRICING_POLICY]: {
    name: 'Pricing policy',
    instruction:
      'Provide a concise overview of the pricing policies adopted by key competitors in the market. Discuss the pricing strategies they employ, whether cost-plus pricing, value-based pricing, competitive pricing, or a combination of these. Highlight any unique aspects of their pricing approach that differentiate them in the market.',
    exampleOne:
      'Employing a competitive pricing strategy, the Company focuses on providing value for money while highlighting its products` unique features and benefits.',
    exampleTwo:
      'Competitor adopts a dynamic pricing strategy, adjusting prices based on factors such as demand and complexity of translation tasks.',
  },
  [KEY_COMPETITORS_NAMES.ANNUAL_SALES_VOLUME]: {
    name: 'Annual sales volume',
    instruction:
      "State the company's annual sales volume by providing the total revenue from sales for the last fiscal year.",
    exampleOne: '5 000 units for 25 mln USD',
    exampleTwo: '7 mln USD',
  },
  [KEY_COMPETITORS_NAMES.MARKET_SHARE]: {
    name: 'Market share',
    instruction: 'Analyze market data to determine company market share.',
    exampleOne: '0.02%',
    exampleTwo: '13%',
  },
  [KEY_COMPETITORS_NAMES.CUSTOMER_LOYALTY_LEVEL]: {
    name: 'Customer loyalty level',
    instruction: "Indicate the customer loyalty level succinctly, highlighting whether it's hight, middle or low.",
    exampleOne: 'high',
    exampleTwo: 'middle',
  },
};

const COMPETITORS_STRENGTHS = {
  [COMPANY_QUALITIES_NAMES.STRENGTH]: {
    name: 'Strength',
    instruction:
      'Identify core competencies and resources giving market edge. Highlight specialized skills, unique capabilities, and brand reputation.',
    exampleOne: ul([
      'Innovative use of biofuel technology in tractor manufacturing',
      'The production site is next to the railway junction',
      'Certificate of environmental compliance',
    ]),
    exampleTwo: ul([
      'An extensive network of professional translators',
      'Secure payment systems',
      'Real-time collaboration tools',
    ]),
  },
};

const COMPETITORS_WEAKNESSES = {
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: {
    name: 'Weakness',
    instruction:
      'Identify company`s internal limitations and challenges. Transparently discuss areas needing improvement: processes, resources, skills. Address gaps in meeting industry benchmarks, operational inefficiencies, and their impact on performance and competitiveness.',
    exampleOne: ul(['Limited product range', 'Higher production costs associated with eco-friendly technology']),
    exampleTwo: ul([
      'Outdated platform interface',
      'Higher pricing compared to competitors',
      'A limited number of translator search tools',
    ]),
  },
};

const COMPETITORS_QUALITIES_INSTRUCTIONS = {
  [COMPANY_QUALITIES_NAMES.NAME]: {
    name: 'Name',
    instruction: 'Provide the intended full and official name of the company.',
    exampleOne: 'AgroTech Innovations',
    exampleTwo: 'TransLink',
  },
  [COMPANY_QUALITIES_NAMES.FILE]: {
    name: 'Logo',
    instruction:
      'Insert the company logo. Ensure the logo is high-resolution, appropriately sized and has transparent background.',
    exampleOne: '',
    exampleTwo: '',
  },
  [COMPANY_QUALITIES_NAMES.STRENGTH]: COMPETITORS_STRENGTHS,
  [COMPANY_QUALITIES_NAMES.WEAKNESSES]: COMPETITORS_WEAKNESSES,
};
const COMPARISON_ITEM_INSTRUCTIONS = {
  [COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON]: {
    [FEATURES_FOR_COMPARISON_NAMES.FEATURE]: {
      name: 'Feature',
      instruction:
        'Introduce the distinctive features and benefits that set your product or service apart. Clearly articulate what makes your offering superior.',
      exampleOne: 'Horsepower',
      exampleTwo: 'Average turnaround time',
    },
  },
  [FEATURES_LIST_NAMES.NAME]: {
    name: 'Name',
    instruction: 'Provide the intended full and official name of the company.',
    exampleOne: 'John Deere',
    exampleTwo: 'LinguaWorld',
  },
  [FEATURES_LIST_NAMES.FILE]: {
    name: '',
    instruction:
      'Insert the company logo. Ensure the logo is high-resolution, appropriately sized and has transparent background.',
    exampleOne: '',
    exampleTwo: '',
  },
  [FEATURES_LIST_NAMES.FEATURE]: {
    name: 'Feature value ',
    instruction: `Quantify your product or service's value to clients by highlighting measurable outcomes, cost savings, or efficiency improvements.`,
    exampleOne: '200',
    exampleTwo: '20 hours',
  },
  [COMPETITORS_COMPARISON_NAMES.COMPETITOR_FEATURES_LIST]: {
    [FEATURES_LIST_NAMES.FILE]: {
      name: '',
      instruction:
        'Insert the company logo. Ensure the logo is high-resolution, appropriately sized and has transparent background.',
      exampleOne: '',
      exampleTwo: '',
    },
    [FEATURES_LIST_NAMES.NAME]: {
      name: 'Name',
      instruction: 'Provide the intended full and official name of the company.',
      exampleOne: 'John Deere',
      exampleTwo: 'LinguaWorld',
    },
    [FEATURES_LIST_NAMES.FEATURE]: {
      name: 'Feature value ',
      instruction: `Quantify your product or service's value to clients by highlighting measurable outcomes, cost savings, or efficiency improvements.`,
      exampleOne: '200',
      exampleTwo: '20 hours',
    },
  },
};

const COMPETITIVE_ANALYSIS_INSTRUCTIONS = {
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS]: {
    name: 'Competitor Identification',
    instruction:
      'List direct and indirect competitors, explaining their relevance. Highlight similarities in offerings, target markets, and distribution channels, emphasizing competitive advantages and market positioning.',
    exampleOne:
      'Direct competitors AgroTech and Harvest Dynamics offer similar heavy tractors. Indirect competitors include GHY, which offers two-wheel tractors and other alternative solutions. These competitors share a common target market of farmers and use identical distribution channels.',
    exampleTwo:
      'Direct competitors include TransLink and LinHub, which offer diverse language services, use machine translation technology, and focus on clients who are natural persons. Indirect competitors include platforms for finding freelancers, which also offer language services. ',
  },
  [COMPETITIVE_ANALYSIS_NAMES.ATTRACTING_COMPETITORS_CUSTOMERS]: {
    name: 'Customer Acquisition',
    instruction:
      'Identify customer migration strategies from competitors. Highlight unique value propositions, pricing advantages, and promotional efforts to entice customers, ensuring differentiation and retention.',
    exampleOne:
      'The Company will offer competitive prices, introductory discounts, and trade-in incentives. It will also engage in digital marketing, industry events, and strategic partnerships.',
    exampleTwo:
      'To attract customers from competitors, the Company will execute an extensive PR campaign across social media platforms and collaborate with bloggers. Enhancing brand visibility will reinforce the company`s position and attract customers through heightened awareness.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.COMPARISON_ITEM]: COMPARISON_ITEM_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.KEY_COMPETITORS]: KEY_COMPETITORS_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.YOUR_COMPANY_QUALITIES]: COMPETITORS_QUALITIES_INSTRUCTIONS,
  [COMPETITIVE_ANALYSIS_NAMES.COMPETITORS_DIFFERENCE]: {
    name: 'Product Differentiation',
    instruction:
      'Compare competing products/services on quality and features. Highlight strengths like durability, performance, or unique functionalities. Emphasize your offerings` advantages to distinguish from competitors.',
    exampleOne:
      'Competitors provide various tractor types with unique features. Some offer ecological biofuel tractors or those made with specific materials. The Company tractors stand out for their reliable design, ensuring superior performance in challenging conditions and soils.',
    exampleTwo:
      'Competitors excel in brand recognition and extensive advertising, attracting a wide customer base. The Company`s platform stands out with its AI-integrated technology, enhancing translation accuracy and speed and thus offering superior performance and efficiency compared to competitors.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.PURCHASING_PRODUCTS]: {
    name: 'Customer Motivation',
    instruction:
      'Analyze factors driving customers to competitors. Consider price, brand reputation, features, and customer service. Identify areas for improvement to better meet customer needs and preferences.',
    exampleOne:
      'Customers purchase competitors` products due to their established brand reputation, competitive pricing, and diverse features. To better meet customer needs, improving pricing strategies, enhancing brand visibility, and offering unique features can be beneficial.',
    exampleTwo:
      'Customers may choose competitors` products due to lower prices and established brand reputation. To compete effectively, the Company must enhance its value proposition by offering competitive pricing and improving customer support.',
  },
  // [COMPETITIVE_ANALYSIS_NAMES.PRODUCT]: {
  //   name: '',
  //   instruction: '',
  //   exampleOne: '',
  //   exampleTwo: '',
  // },
  [COMPETITIVE_ANALYSIS_NAMES.FUTURE_EXPECTATIONS]: {
    name: 'Future Competition',
    instruction:
      'Evaluate potential market entrants. Consider industry attractiveness, barriers to entry, and market gaps. Highlight threats such as increased competition, price wars, and loss of market share.',
    exampleOne:
      'Technological advancements and changing consumer preferences will influence future competition. Globalization and growing markets may lead to increased competition. Established players` strong market presence and brand recognition may create barriers to entry into new companies.',
    exampleTwo:
      'Potential market entrants face low entry barriers due to the market`s global nature but encounter intense competition. The threat lies in the difficulty of establishing a brand presence amidst established competitors and the need for innovative solutions to address market gaps.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.BARRIERS]: {
    name: 'Entry Barriers',
    instruction:
      'Identify market entry barriers. Discuss factors like high capital requirements, regulatory hurdles, brand loyalty, and economies of scale, deterring new entrants.',
    exampleOne:
      'Terms of market entry include high capital requirements for R&D and production. High competition players` strong market presence and brand recognition may create barriers to entry into new companies.',
    exampleTwo:
      'New entrants face several challenges, including a high initial investment in tech infrastructure and building a reliable translator network.',
  },
  [COMPETITIVE_ANALYSIS_NAMES.CHANGING_OF_CURRENT_MARKET]: {
    name: 'Market Disruptors',
    instruction:
      'Anticipate potential market disruptors. Assess emerging technologies, changing consumer preferences, and regulatory shifts. Highlight innovations that could reshape industry dynamics and threaten existing market players.',
    exampleOne:
      'The emergence of fully robotic tractors equipped with AI technology poses a significant threat to existing market players. This innovation could reshape industry dynamics by reducing reliance on human labour in agriculture, potentially disrupting traditional tractor manufacturers.',
    exampleTwo:
      'The advent of AI-driven simultaneous translation poses a significant disruption, potentially reducing the demand for human translation services. This innovation may reshape industry dynamics by offering faster, more cost-effective solutions, challenging existing market players to adapt.',
  },
};

const CONSUMER_NEEDS_INSTRUCTIONS = {
  [CONSUMER_NEEDS_NAMES.B2C]: {
    [B2C_NAMES.GENDER]: {
      name: 'Gender',
      instruction: "Indicate the consumer gender succinctly, highlighting whether it's male, female of gender-neutral.",
      exampleOne: '',
      exampleTwo: '',
    },
    [B2C_NAMES.AGE]: {
      name: 'Age',
      instruction:
        'Identify the consumer age range by analyzing demographic data and market research findings. Ensure accuracy and relevance to target audience preferences.',
      exampleOne: '25-65',
      exampleTwo: '18-45',
    },
    [B2C_NAMES.ANNUAL_INCOME]: {
      name: 'Income',
      instruction:
        'Identify the consumer income levels by analyzing market demographics and purchasing power indicators. Ensure accuracy and relevance to target market segmentation.',
      exampleOne: 'from 7 thousand USD',
      exampleTwo: '2-10 thousand USD',
    },
    [B2C_NAMES.PROFESSION]: {
      name: 'Profession',
      instruction:
        'Identify the consumer professions through market research and demographic analysis. Ensure relevance and accuracy in identifying target audience occupations.',
      exampleOne: 'Farmer',
      exampleTwo: 'Students, employees in multinational corporations, academia and the tourism industry',
    },
    [B2C_NAMES.PERSONAL_INTERESTS]: {
      name: 'Personal interests',
      instruction:
        'Detail target audience personal interests. Use demographic insights to identify hobbies, preferences, and lifestyle choices influencing their purchasing decisions.',
      exampleOne: 'Include agriculture, machinery, technology, environmental conservation, soil health.',
      exampleTwo: 'Interested in foreign cultures, technology, and efficient communication',
    },
    [B2C_NAMES.PURCHASING_HABITS]: {
      name: 'Purchasing habits',
      instruction:
        'Analyze target audience purchasing habits. Utilize consumer data to identify buying frequency, preferred channels, and factors influencing purchase decisions.',
      exampleOne:
        'Customers typically purchase 1-2 units every 7 years. Purchase decisions are influenced by cost-effectiveness, durability, and performance, with preferred channels varying in accessibility and convenience.',
      exampleTwo: 'They buy services online and prefer subscription models.',
    },
    [B2C_NAMES.NEEDS_AND_PROBLEMS]: {
      name: 'Needs and problems',
      instruction:
        'Identify target audience needs and problems. Utilize market research to pinpoint pain points and unmet needs, offering solutions that address their challenges effectively.',
      exampleOne:
        'Customers operating in heavy and wet soils require reliable and durable tractors that can effectively work in challenging conditions without harming the soil.',
      exampleTwo:
        'Face language barriers that prevent effective interaction. The target audience seeks an online solution that provides fast and reliable language support to navigate different language environments easily.',
    },
  },
  [CONSUMER_NEEDS_NAMES.B2B]: {
    [B2B_NAMES.INDUSTRY]: {
      name: 'Industry',
      instruction:
        'Identify the industry of your ideal business customer. Select the most applicable sector or describe the industry if it does not appear in the provided options. Specify the region where your business customers main operations are based. If they operate in multiple regions, please list the primary region of business activity.',
      exampleOne: 'Agriculture',
      exampleTwo: 'Multinational Corporations, Tech, Healthcare, Legal, Education.',
    },
    [B2B_NAMES.ANNUAL_REVENUE]: {
      name: 'Annual revenue',
      instruction:
        "Specify the annual revenue range of your typical business customer. Identify the customer's annual revenue by analyzing financial statements and income reports.",
      exampleOne: ' 60M-120M USD',
      exampleTwo: '$10M-$500M',
    },
    [B2B_NAMES.NUMBER_OF_EMPLOYERS]: {
      name: 'Number of Employees',
      instruction:
        "State the usual number of employees working at your typical customer's company. Include all types of employees such as full-time, part-time, and contractual staff.",
      exampleOne: '300-900',
      exampleTwo: '250+',
    },
    [B2B_NAMES.REGION]: {
      name: 'Region',
      instruction:
        'Mention the primary geographical region or market in which your typical business customer operates. If they are active in multiple regions, list the most significant ones.',
      exampleOne: 'USA',
      exampleTwo: 'North America, Europe, Asia-Pacific',
    },
    [B2B_NAMES.PURCHASING_CYCLE]: {
      name: 'Purchasing cycle',
      instruction:
        'Describe the purchasing behavior of your ideal business customer, including the frequency and timing of their major buying decisions.',
      exampleOne: 'Every 5-7 years',
      exampleTwo: 'Quarterly/Yearly.',
    },
  },
  [CONSUMER_NEEDS_NAMES.PURCHASING_DECISION]: {
    name: 'Purchase Decisions',
    instruction:
      'If your customers are legal entities, clarify the decision-making structure within these organizations. Specify who holds the authority to make purchasing decisions, and if applicable, highlight the roles of influencers or stakeholders in the process. ',
    exampleOne:
      'Owners or senior management typically make purchase decisions. Equipment specialists may sometimes influence the decision-making process by providing recommendations or insights.',
    exampleTwo:
      'Purchase decisions are typically made by department heads or procurement managers in businesses and by project managers or administrators in government agencies.',
  },
  [CONSUMER_NEEDS_NAMES.INFLUENCING_FACTORS]: {
    name: 'Influencing Factors',
    instruction:
      'Identify and elaborate on the key factors influencing the purchase decision-making process for your customers. It may include considerations such as product features, pricing, brand reputation, customer reviews, or specific needs that your product or service addresses. Provide a comprehensive overview of the factors that potential customers prioritize when deciding to make a purchase.',
    exampleOne:
      'Product reliability, durability, performance, and cost-effectiveness are key factors influencing purchase decisions. Customers prioritize features that address their needs, such as working in heavy and wet soils.',
    exampleTwo:
      'Key factors influencing purchase decisions include service quality, language coverage, integration ease, provider reputation, flexibility.',
  },
  [CONSUMER_NEEDS_NAMES.DIFFERENCES]: {
    name: 'Behavioral Difference',
    instruction:
      'Outline any significant differences in consumer behavior or preferences across various customer segments. Highlight specific factors that differentiate one segment, such as purchasing habits, product preferences, or response to marketing strategies.',
    exampleOne:
      'Agricultural companies prioritize bulk purchases and value efficiency, whereas private farms often opt for individual units and prioritize reliability. This difference in buying behavior impacts production plans according to demand.',
    exampleTwo:
      'Businesses prioritize fast translations, while government agencies focus on comprehensive language solutions for official documents. Businesses respond well to targeted marketing, emphasizing accuracy and timeliness, whereas government agencies value credibility and security.',
  },
  [CONSUMER_NEEDS_NAMES.HOW_CUSTOMERS_SATISFIED]: {
    name: 'Market Satisfaction',
    instruction:
      'Provide an overview of the current customer satisfaction levels within the market. Discuss the overall sentiment of customers toward existing offerings and services. Highlight specific areas where customers express satisfaction and those where improvements are needed. It could include product features, service quality, pricing, or other factors influencing customer perceptions.',
    exampleOne:
      'Customer satisfaction with existing products varies. While some clients are satisfied with features and performance, others seek improvements in durability and pricing. Overall sentiment suggests a mix of contentment and demand for enhancements.',
    exampleTwo:
      'Customer satisfaction with existing services in the market is moderate. Some clients express dissatisfaction with complex registration processes and service accessibility. Simplifying procedures and enhancing convenience could improve overall satisfaction levels.',
  },
  [CONSUMER_NEEDS_NAMES.DEMAND]: {
    name: 'Demand Creation',
    instruction:
      'Analyze whether the business operates in a market where demand is already established or if it is pioneering new demand through innovative products or services.',
    exampleOne:
      'The Company operates in a market with established demand. Tractors are essential agricultural equipment, and the ongoing need for mechanized farming solutions drives demand. The business may innovate within this existing demand but is not pioneering an entirely new market.',
    exampleTwo:
      'The Company is pioneering new demand by introducing an online platform for finding translators. This innovative service is not yet established in the market, offering a unique solution to meet customer needs.',
  },
  [CONSUMER_NEEDS_NAMES.CUSTOMER_BASE_SIZE]: {
    name: 'Customer Base Size',
    instruction:
      'Provide an overview of the current customer base, emphasizing the number of customers the business has acquired. Discuss the level of interest the product has garnered, citing specific instances or indicators of customer engagement. It may include inquiries, sign-ups, or pre-orders.',
    exampleOne:
      'The Company has over 800 farms as customers. In 2022, customer numbers increased by 12% from the previous year, indicating growing interest and engagement with the product. Additionally, 40% of customers are repeat customers.',
    exampleTwo:
      'The platform currently has a customer base of 20,000 registered translators globally, with a monthly influx of 100 new registrations. In 2022, it facilitated 50,000 orders, indicating a 23% increase from the previous year.',
  },
  [CONSUMER_NEEDS_NAMES.SURVEY]: {
    name: 'Product Launch Feedback',
    instruction:
      'Introduce the customer survey initiative and outlining the purpose and scope of the survey. Specify the target audience and the methodology employed to gather insights. Provide a brief overview of the critical questions asked in the survey. Present a concise summary of the survey findings, highlighting key trends, preferences, and sentiments of potential customers.',
    exampleOne:
      'The customer survey initiative assesses post-sale satisfaction levels. It targets recent buyers and employs telephone interviews. The results show that 80% of customers express satisfaction and intend to repeat purchases.',
    exampleTwo:
      'The Company conducts a survey customer survey to gauge satisfaction. The survey aims to gather customer feedback on service improvements, utilizing online questionnaires. Findings highlight demands for more payment options and a mobile application platform, indicating areas for enhancement.',
  },
};

export const MARKET_ANALYSIS_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.MARKET_OVERVIEW]: MARKET_OVERVIEW_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.COMPETITIVE_ANALYSIS]: COMPETITIVE_ANALYSIS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MARKET_ANALYSIS.CONSUMER_NEEDS]: CONSUMER_NEEDS_INSTRUCTIONS,
};
